import { getDealPathname } from '@/components/nav/utils/url-builders';
import { Button } from '@/components/ui/button';
import useHubs from '@/hooks/use-hubs';
import { cn } from '@/lib/utils';
import { Trade } from '@/models/registry';
import { type ResellType } from '@/models/types';
import { useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { type ReactNode } from 'react';
import TradeVehicleEditor from '../trade/vehicle/trade-vehicle-editor';
import PrimaryCustomerNameEditor from './primary-customer/primary-customer-name-editor';

const Row = ({ children }: { children: ReactNode }) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">{children}</div>
  );
};

const Colummn = ({
  children,
  align,
}: {
  children: ReactNode;
  align: 'start' | 'end';
}) => {
  return (
    <div
      className={cn('flex flex-row', {
        'justify-start': align === 'start',
        'lg:justify-end': align === 'end',
      })}
    >
      <div className="flex flex-col gap-2 w-full min-w-56 max-w-96">
        {children}
      </div>
    </div>
  );
};

const TradeMobileMain = observer(({ trade }: { trade: Trade }) => {
  const navigate = useNavigate();
  const { hubPathIdentifier } = useHubs();
  const shortId = trade.deal?.short_id;
  if (!shortId) return;

  return (
    <div className="flex flex-col gap-2 p-4 h-[calc(100vh-40px)] overflow-auto w-full dark:bg-dark">
      <Button
        variant="ghost"
        className="h-4 w-4 p-8"
        onClick={() =>
          navigate({ to: getDealPathname(shortId, hubPathIdentifier) })
        }
      >
        <ArrowLeft />
        <span className="text-sm">{shortId}</span>
      </Button>

      <Row>
        <Colummn align="start">
          <div className="flex flex-col gap-1">
            <PrimaryCustomerNameEditor
              id={trade.id}
              fullName={trade.full_name}
              shortId={trade.short_id}
            />
          </div>
        </Colummn>
      </Row>

      <div className="w-full border-t"></div>
      <Row>
        <Colummn align="start">
          <h5 className="scroll-m-20 text-lg  tracking-tight w-full">
            Vehicle
          </h5>
          <TradeVehicleEditor
            tradeId={trade.id}
            vin={trade.vin}
            make={trade.make}
            model={trade.model}
            trim={trade.trim}
            year={trade.year}
            mileage={trade.mileage}
            condition={trade.condition}
            numberOfKeys={trade.number_of_keys}
            exteriorColor={trade.exterior_color}
            interiorColor={trade.interior_color}
            titlePresent={trade.title_present}
            lienholder={trade.lienholder || ''}
            payoffAmount={trade.payoff_amount || 0}
            appraisedValue={trade.appraised_value || 0}
            resellType={trade.resell_type as ResellType}
          />
        </Colummn>
      </Row>
    </div>
  );
});

export default TradeMobileMain;
